/**
 * screens/HomeScreen.tsx
 * Operator Home — 6-col KPI grid + Recent calls / Live now + guaranteed-floor.
 * UI recreated verbatim from the design HTML (lines ~120-172), max-width 1120.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { useLiveCalls } from '../context/liveCallsContext';
import Icon from '../components/icon';
import { Card, KpiCard, ScreenBody, outcomeColor } from '../components/voiceUi';
import { FadeIn, KpiSkeleton, RowSkeleton } from '../components/skeleton';
import { CallRow } from '../data/voiceMock';
import { listCalls, getCallLogKpis, getOpenFollowupCount, getAnalytics } from '../services/voice/voiceService';
import { useCachedRead } from '../services/voice/cachedRead';
import { fmtDur } from '../utils/format';

// Fixed placeholder counts so the skeleton occupies exactly the space the real
// content will: six KPI tiles, five recent-call rows (listCalls limit is 5).
const KPI_SLOTS = [0, 1, 2, 3, 4, 5];
const RECENT_SLOTS = [0, 1, 2, 3, 4];

// HTML outcome → chip label (completed / missed / caller dropped).
function outLabel(o: string): string {
  if (o === 'completed') return 'completed';
  if (o === 'missed') return 'missed';
  return 'caller dropped';
}

// Colored outcome dot (green/amber/red) — shared hue via outcomeColor.
function Dot({ outcome }: { outcome: string }) {
  const c = useTheme().theme.colors;
  return <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: outcomeColor(outcome, c) }} />;
}

// Direction arrow (inbound = down · outbound = up) — fixed-width slot so rows
// stay aligned even when a live call hasn't reported its direction yet.
function DirArrow({ dir }: { dir?: 'inbound' | 'outbound' }) {
  const c = useTheme().theme.colors;
  return (
    <View style={{ width: 13, alignItems: 'center' }}>
      {dir ? <Icon name={dir === 'inbound' ? 'arrow-d' : 'arrow-u'} size={13} color={c.ink3} /> : null}
    </View>
  );
}

// Pull a KPI value from the analytics response by (fuzzy) label, else a fallback.
function pickKpi(kpis: { label: string; value: string; sub: string }[], match: string, fallback: string): { value: string; sub: string } {
  const hit = kpis.find(k => k.label.toLowerCase().includes(match.toLowerCase()));
  return hit ? { value: hit.value, sub: hit.sub } : { value: fallback, sub: '' };
}

export default function HomeScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { go, openCall, liveTick, orgId } = useConsole();
  // Live-now reads the SAME SSE feed as the Live Calls screen (shell-level
  // context) so the Home tile + mini list reflect active calls in real time.
  const { calls: liveCalls } = useLiveCalls();

  // Home composes four INDEPENDENT reads. They used to sit behind one
  // Promise.all and a single full-screen spinner, so the slowest endpoint hid
  // the other three and a revisit re-fetched everything from blank. Each read
  // now lands on its own and is cached per-org, so the screen paints its last
  // known state on the first frame and fills in as answers arrive.
  //
  // "Calls today" must be windowed to today — the log-summary endpoint returns
  // ALL-TIME when no range is passed, so without this the card showed the
  // all-time total mislabeled as today. Range = local midnight → now. Memoised
  // on orgId, not on every render, so the read isn't re-issued continuously.
  const todayRange = useMemo(() => {
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    return { from: dayStart.toISOString(), to: new Date().toISOString() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const callsRead = useCachedRead<CallRow[]>('home.recent', orgId, useCallback(() => listCalls({ limit: 5 }), []));
  const openRead = useCachedRead<number>('home.openFollowups', orgId, getOpenFollowupCount);
  const analyticsRead = useCachedRead('home.analytics', orgId, getAnalytics);
  const logRead = useCachedRead('home.logKpis', orgId, useCallback(() => getCallLogKpis(todayRange), [todayRange]));

  const recent = useMemo(() => (callsRead.data ?? []).slice(0, 5), [callsRead.data]);
  const openCount = openRead.data ?? 0;

  // Map the 6 Home cards from real reads; analytics provides completion/upsell/
  // handle-time, call-log KPIs provide today's totals, followups the open count.
  // A tile renders as soon as ITS source is in — it no longer waits on the rest.
  const kpis = useMemo(() => {
    const a = analyticsRead.data?.kpis ?? [];
    const k = logRead.data;
    const dash = { value: '—', sub: '' };
    return [
      k
        ? { label: 'Calls today', value: String(k.total ?? 0), sub: `${k.completedN ?? 0} completed` }
        : { label: 'Calls today', ...dash },
      { ...(a.length ? pickKpi(a, 'completion', '—') : dash), label: 'Completion rate', sub: 'no human needed' },
      // Value is overridden at render from the live SSE feed (see liveNowCount).
      { label: 'Live now', value: '0', sub: 'monitor' },
      { label: 'Open follow-ups', value: String(openCount), sub: 'awaiting action' },
      {
        ...(a.length ? pickKpi(a, 'upsell revenue', '—') : dash),
        label: 'Upsell accepted',
        sub: (a.length ? pickKpi(a, 'upsell hit', '').value : '') || 'hit rate',
      },
      { ...(a.length ? pickKpi(a, 'avg handle', '—') : dash), label: 'Avg handle time', sub: 'across all calls' },
    ];
  }, [analyticsRead.data, logRead.data, openCount]);

  // The KPI strip shows skeletons only while BOTH of its sources are still
  // unknown; once either lands there is real content worth showing.
  const kpisPending = analyticsRead.loading && logRead.loading;

  // Live-now from the SSE feed. durSec = real elapsed since the call's first frame,
  // recomputed each render. Reading liveTick forces that recompute every second.
  void liveTick;
  const liveNowCount = liveCalls.length;
  const liveMini = liveCalls.map(lc => ({
    id: lc.id,
    contact: lc.num,
    dir: lc.dir,
    durSec: Math.max(0, Math.floor((Date.now() - lc.startedAt) / 1000)),
  }));

  // No full-screen spinner: the layout is rendered immediately and each region
  // shows a skeleton of its own shape until its data lands. Nothing moves when
  // the real content swaps in, because the placeholder already holds the space.
  return (
    <ScreenBody maxWidth={1120}>
      {/* 6-col KPI grid, gap 12 */}
      <View style={{ flexDirection: 'row', gap: 12, marginBottom: 20 }}>
        {kpisPending
          ? KPI_SLOTS.map(i => <View key={i} style={{ flex: 1 }}><KpiSkeleton /></View>)
          : kpis.map(k => (
            <View key={k.label} style={{ flex: 1 }}>
              {/* Live now is driven by the SSE feed, not the async load. */}
              <FadeIn>
                <KpiCard label={k.label} value={k.label === 'Live now' ? String(liveNowCount) : k.value} sub={k.sub} />
              </FadeIn>
            </View>
          ))}
      </View>

      {/* 1.35fr / 1fr two-col row */}
      <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
        {/* LEFT: Recent calls */}
        <View style={{ flex: 1.35 }}>
          <Card>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: c.line }}>
              <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink, fontFamily: theme.font.sans }}>Recent calls</Text>
              <Pressable onPress={() => go('log')} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <Text style={{ color: c.ink3, fontSize: 12, fontFamily: theme.font.sans }}>View all</Text>
                <Icon name="v-chev-r" size={13} color={c.ink3} />
              </Pressable>
            </View>
            <View>
              {callsRead.loading
                ? RECENT_SLOTS.map(i => <RowSkeleton key={i} last={i === RECENT_SLOTS.length - 1} />)
                : recent.length === 0
                  ? (
                    <View style={{ paddingHorizontal: 16, paddingVertical: 14 }}>
                      <Text style={{ fontSize: 12, color: c.ink4, fontFamily: theme.font.sans }}>No calls yet.</Text>
                    </View>
                  )
                  : (
                    <FadeIn>
                      {recent.map((cl, i) => (
                        <Pressable key={cl.id} onPress={() => openCall(cl.id, { id: cl.id, contact: cl.contact, dir: cl.dir, outcome: cl.outcome, durSec: cl.durSec })}
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 11, borderBottomWidth: i === recent.length - 1 ? 0 : 1, borderBottomColor: c.line }}>
                          <Text style={{ fontFamily: theme.font.mono, fontSize: 11, color: c.ink3, width: 52 }}>{cl.when}</Text>
                          <DirArrow dir={cl.dir} />
                          <Text numberOfLines={1} style={{ fontSize: 12.5, fontWeight: '500', width: 120, color: c.ink, fontFamily: theme.font.sans }}>{cl.contact}</Text>
                          <Text numberOfLines={1} style={{ flex: 1, fontSize: 12.5, color: c.ink3, fontFamily: theme.font.sans }}>{cl.products.length ? cl.products.join(', ') : '—'}</Text>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            <Dot outcome={cl.outcome} />
                            <Text style={{ fontSize: 12, color: c.ink2, fontFamily: theme.font.sans }}>{outLabel(cl.outcome)}</Text>
                          </View>
                        </Pressable>
                      ))}
                    </FadeIn>
                  )}
            </View>
          </Card>
        </View>

        {/* RIGHT column */}
        <View style={{ flex: 1, gap: 16 }}>
          <Card>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: c.line }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <View style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: c.ink }} />
                <Text style={{ fontWeight: '600', fontSize: 13.5, color: c.ink, fontFamily: theme.font.sans }}>Live now</Text>
              </View>
              <Pressable onPress={() => go('live')}>
                <Text style={{ color: c.ink3, fontSize: 12, fontFamily: theme.font.sans }}>Monitor</Text>
              </Pressable>
            </View>
            {liveMini.length === 0 ? (
              <View style={{ paddingHorizontal: 16, paddingVertical: 14 }}>
                <Text style={{ fontSize: 12, color: c.ink4, fontFamily: theme.font.sans }}>No live calls right now.</Text>
              </View>
            ) : liveMini.map((l, i) => (
              <View key={l.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: i === liveMini.length - 1 ? 0 : 1, borderBottomColor: c.line }}>
                <DirArrow dir={l.dir} />
                <Text style={{ fontFamily: theme.font.mono, fontSize: 12, width: 104, color: c.ink }}>{l.contact}</Text>
                <Text numberOfLines={1} style={{ flex: 1, fontSize: 12, color: c.ink3, fontFamily: theme.font.sans }}>live</Text>
                <Text style={{ fontFamily: theme.font.mono, fontSize: 11, color: c.ink3 }}>{fmtDur(l.durSec)}</Text>
              </View>
            ))}
          </Card>

          {/* Black guaranteed-floor card */}
          <View style={{ backgroundColor: c.ink, borderRadius: 10, paddingHorizontal: 17, paddingVertical: 16 }}>
            <Text style={{ fontSize: 12, color: c.paper, opacity: 0.7, marginBottom: 6, fontFamily: theme.font.sans }}>Guaranteed floor</Text>
            <Text style={{ fontSize: 14, fontWeight: '500', lineHeight: 14 * 1.4, color: c.paper, marginBottom: 12, fontFamily: theme.font.sans }}>Every request the agent can’t resolve lands as a follow-up — never lost.</Text>
            <Pressable onPress={() => go('followups')} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 32, paddingHorizontal: 13, borderRadius: 7, backgroundColor: c.paper, alignSelf: 'flex-start' }}>
              <Text style={{ fontWeight: '600', fontSize: 12.5, color: c.ink, fontFamily: theme.font.sans }}>Open follow-ups</Text>
              <Text style={{ fontFamily: theme.font.mono, fontSize: 12.5, fontWeight: '600', color: c.ink }}>{openCount}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </ScreenBody>
  );
}
